/**
 * LoadingService - Abstraction layer for full-screen loading indicators
 *
 * Provides a unified API that works with PrimeVue loading overlays.
 *
 * Usage:
 *   const loader = this.$loading.open({ message: 'Loading...' });
 *   // ... do async work
 *   loader.close();
 *
 * @class LoadingService
 */
class LoadingService {
    constructor() {
        this.app = null;
        this.activeLoaders = new Map();
        this.loaderId = 0;
    }

    /**
     * Initialize the service with the Vue app instance
     * @param {Object} app - Vue app instance
     */
    install(app) {
        this.app = app;
        app.config.globalProperties.$loading = this;
        app.provide('loading', this);
    }

    /**
     * Open a loading overlay
     *
     * @param {Object} params - Loading configuration
     * @param {string} [params.container] - CSS selector for container (default: body)
     * @param {boolean} [params.isFullPage=true] - Whether to cover the full page
     * @param {string} [params.message] - Optional loading message
     * @returns {Object} Loader instance with close() method
     */
    open(params = {}) {
        if (!this.app) {
            console.error('LoadingService not initialized. Call init(app) first.');
            return { close: () => {} };
        }

        if (typeof window === 'undefined') {
            return { close: () => {} };
        }

        return this.openPrimeVueLoading(params);
    }

    /**
     * Open PrimeVue BlockUI loading overlay
     * @private
     */
    openPrimeVueLoading(params) {
        const loaderId = ++this.loaderId;

        // Create a reactive state for this loader
        const loaderState = {
            id: loaderId,
            blocked: true,
            message: params.message || null,
        };

        // Store in active loaders
        this.activeLoaders.set(loaderId, loaderState);

        // Emit event to trigger BlockUI component
        window.dispatchEvent(
            new CustomEvent('primevue-loading-open', {
                detail: loaderState,
            })
        );

        return {
            close: () => {
                loaderState.blocked = false;
                this.activeLoaders.delete(loaderId);
                window.dispatchEvent(
                    new CustomEvent('primevue-loading-close', {
                        detail: { id: loaderId },
                    })
                );
            },
        };
    }

    /**
     * Check if any loading overlay is currently active
     * @returns {boolean}
     */
    get isActive() {
        return this.activeLoaders.size > 0;
    }

    /**
     * Close all active loading overlays
     */
    closeAll() {
        this.activeLoaders.forEach(loader => {
            loader.blocked = false;
        });
        this.activeLoaders.clear();
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('primevue-loading-close-all'));
        }
    }
}

// Create singleton instance
const loadingService = new LoadingService();

// Vue plugin
export default loadingService;
